/**
 * bootScene.ts
 *
 * Timing constants + copy for BootAnimation, separated from the orchestration
 * component — same split used by DesktopPreview's desktopScene.ts. All values
 * in milliseconds unless noted.
 */

export const BOOT_TIMING = {
  /** Minimum time to hold on the 'loading' stage even if the real Space is already ready. */
  minLoadingHoldMs: 3000,
  /** Duration of the CRT-style top-to-bottom reveal wipe played at the start of every stage. */
  crtRevealMs: 500,
  /** Duration of one bar-fill loop cycle in the 'loading' stage — it repeats indefinitely until ready. */
  loaderMs: 2600,
  /** How long each status phrase is shown before cycling to the next (loops for as long as 'loading' lasts). */
  statusPhraseMs: 650,
  /** How long the 'greeting' ("Hi, I'm River") stage holds before advancing to 'mounting'. */
  greetingMs: 2800,
  /** Total hold time when `prefers-reduced-motion` is set for the greeting stage — one static frame, no wipe/scanline. */
  reducedMotionHoldMs: 900,

  // ── 'mounting' stage — desktop mounts underneath, overlay stays fully opaque ────
  /**
   * Backstop only — the real trigger out of 'mounting' is
   * `signupBootStore.desktopReady` going true (see SpacePageWrapper.tsx's
   * isUIReady signal). If the desktop somehow never reports ready (a route
   * that doesn't mount SpacePageWrapper, a stalled init), this fires so the
   * overlay still gets hidden instead of leaving the user staring at a black
   * screen forever.
   */
  desktopReadyMaxWaitMs: 6000,

  // ── 'revealing' stage — the desktop is revealed instantly ──────────────────
  // After the greeting, the boot overlay hides itself all at once (see
  // .revealing in BootAnimation.module.scss), uncovering the real desktop
  // chrome (mounted underneath it the moment this stage starts). The two
  // timings below then pace the choreography beats that follow.
  /**
   * Beat held on the bare, chrome-less desktop (system bar only) right after
   * it's revealed and BEFORE the dock/sidebar/window-surface/chat start their
   * slide-in — so the reveal reads as a finished step of its own rather than
   * immediately spilling into the next one.
   */
  postRevealPauseMs: 300,
  /** Extra pause after the reveal before the chat panel starts its own slide-in. */
  chatEntranceDelayMs: 250,
  /** Must match SpaceChat.scss's `onboardingChatEmerge` animation-duration. */
  chatEntranceMs: 480,
} as const;

export const BOOT_COPY = {
  statusPhrases: [
    'Setting up your desktop…',
    'Personalizing your workspace…',
    'Waking up the orb…',
    'Almost there…',
  ],
  greeting: "Hi, I'm River 👋",
} as const;
